import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, getDocs, doc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { getAppointmentTypeLabel } from './appointmentTypes';
import { isSundayDateString, isWithinOpenHours } from './bookingSchedule';
import './TimeSelectModal.css';

const TimeSelectModal = ({ 
  isOpen, 
  onClose, 
  onTimeSelect, 
  selectedDate, 
  selectedLocation, 
  appointmentType 
}) => {
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [occupiedTimeSlots, setOccupiedTimeSlots] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for cleanup
  const unsubscribeRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const pageLoadId = useRef(`page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const activeSessionIdRef = useRef(null); // Track active session for cleanup

  // Helper function to normalize time format (handle both 24-hour and 12-hour formats)
  const normalizeTimeFormat = (timeString) => {
    // If already in 12-hour format (contains AM/PM), return as is
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // Otherwise, assume it's in 24-hour format and convert
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Generate time slots from 07:00 to 18:00 with filtering based on appointment type
  // Normal (free): 15-minute intervals including :00 and :30
  // Special: :00 and :30 only
  const generateTimeSlots = () => {
    const slots = [];
    const now = new Date();
    
    // Get today's date in local timezone YYYY-MM-DD format
    const today = now.getFullYear() + '-' + 
                  String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(now.getDate()).padStart(2, '0');
    
    // Only apply time filtering if the selected date is today
    let shouldFilterByTime = false;
    let currentTime = 0;
    let thirtyMinutesFromNow = 0;
    
    if (selectedDate === today) {
      shouldFilterByTime = true;
      currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
      thirtyMinutesFromNow = currentTime + 30; // 30 minutes from now
      
      console.log('🔍 Time filtering enabled for today:', {
        selectedDate,
        today,
        currentTime: `${Math.floor(currentTime/60)}:${currentTime%60}`,
        thirtyMinutesFromNow: `${Math.floor(thirtyMinutesFromNow/60)}:${thirtyMinutesFromNow%60}`
      });
    } else {
      console.log('🔍 Time filtering disabled - future date:', {
        selectedDate,
        today
      });
    }

    const interval = appointmentType === 'special' ? 10 : 15;
    const sunday = isSundayDateString(selectedDate);
    
    for (let hour = 7; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const hour24 = hour;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        const hour12 = hour24 % 12 || 12;
        const timeString = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        
        // Remove 7:00 AM completely
        if (hour24 === 7 && minute === 0) {
          continue;
        }

        // Sunday: 11:15 AM – 6:50 PM; weekdays: after 7:00 – 6:50 PM
        if (!isWithinOpenHours(hour24, minute, sunday)) {
          continue;
        }
        
        // Special appointments: only show :30 and :00 times
        if (appointmentType === 'special') {
          if (minute !== 30 && minute !== 0) {
            continue;
          }
        }
        
        // Only filter out past times and times within 30 minutes if the selected date is today
        if (shouldFilterByTime) {
          const slotTimeInMinutes = hour24 * 60 + minute;
          if (slotTimeInMinutes <= thirtyMinutesFromNow) {
            console.log(`⏰ Filtering out time slot ${timeString} (${slotTimeInMinutes} <= ${thirtyMinutesFromNow})`);
            continue;
          }
        }
        
        slots.push(timeString);
      }
    }
    
    console.log('✅ Generated time slots:', {
      totalSlots: slots.length,
      slots: slots.slice(0, 5), // Show first 5 slots
      appointmentType,
      shouldFilterByTime
    });
    
    return slots;
  };

  // Check occupied time slots in real-time
  const checkOccupiedTimeSlots = async () => {
    if (!selectedDate || !selectedLocation || !appointmentType) {
      return;
    }

    try {
      // Query orders for the selected date, location, and appointment type
      const ordersQuery = query(
        collection(db, 'orders'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', selectedLocation),
        where('appointmentType', '==', appointmentType),
        where('status', 'in', ['confirmed', 'trading'])
      );

      // Query active sessions for the selected date, location, and appointment type
      const activeSessionsQuery = query(
        collection(db, 'activeBookings'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', selectedLocation),
        where('appointmentType', '==', appointmentType)
      );

      const [ordersSnapshot, sessionsSnapshot] = await Promise.all([
        getDocs(ordersQuery),
        getDocs(activeSessionsQuery)
      ]);

      const orderCounts = {};
      const sessionCounts = {};
      const currentUserSessions = {};

      // Count orders
      ordersSnapshot.forEach(doc => {
        const order = doc.data();
        const timeSlot = normalizeTimeFormat(order.appointmentTime);
        orderCounts[timeSlot] = (orderCounts[timeSlot] || 0) + 1;
      });

      // Count sessions
      sessionsSnapshot.forEach(doc => {
        const session = doc.data();
        const timeSlot = normalizeTimeFormat(session.appointmentTime);
        sessionCounts[timeSlot] = (sessionCounts[timeSlot] || 0) + 1;
        
        // Track current user's sessions
        if (session.userId === auth.currentUser?.uid) {
          currentUserSessions[timeSlot] = (currentUserSessions[timeSlot] || 0) + 1;
        }
      });

      // Determine which time slots are occupied
      const occupied = new Set();
      const allTimeSlots = generateTimeSlots();
      
      allTimeSlots.forEach(timeSlot => {
        const orderCount = orderCounts[timeSlot] || 0;
        const sessionCount = sessionCounts[timeSlot] || 0;
        const currentUserSessionCount = currentUserSessions[timeSlot] || 0;
        
        // Calculate blocking counts (excluding current user's sessions)
        const blockingOrderCount = orderCount;
        const blockingSessionCount = sessionCount - currentUserSessionCount;
        const totalBlockingUsers = blockingOrderCount + blockingSessionCount;

        // Apply limits based on appointment type
        const maxUsers = appointmentType === 'special' ? 3 : 1; // Special: 3 users, Normal/free: 1 user per slot
        
        if (totalBlockingUsers >= maxUsers) {
          occupied.add(timeSlot);
        }
      });

      setOccupiedTimeSlots(occupied);
      
      // Update available time slots
      const available = allTimeSlots.filter(slot => !occupied.has(slot));
      setAvailableTimeSlots(available);

    } catch (error) {
      console.error('Error checking occupied time slots:', error);
      setError('Failed to load time slots. Please try again.');
    }
  };

  // Cleanup active session when modal closes (synchronous version)
  const cleanupActiveSession = () => {
    if (!activeSessionIdRef.current) return;
    
    const sessionId = activeSessionIdRef.current;
    console.log('🧹 TimeSelectModal: Cleaning up active session:', sessionId);
    
    try {
      const sessionDocRef = doc(db, 'activeBookings', sessionId);
      // Fire and forget - don't wait for completion
      deleteDoc(sessionDocRef).then(() => {
        console.log('✅ Active session deleted:', sessionId);
      }).catch((error) => {
        console.error('⚠️ Error deleting session:', error);
      });
      activeSessionIdRef.current = null;
    } catch (error) {
      console.error('⚠️ Error cleaning up active session:', error);
    }
  };

  // Create active session for selected time slot
  const createActiveSession = async (timeSlot) => {
    if (!auth.currentUser || !selectedDate || !selectedLocation || !appointmentType) {
      return null;
    }

    try {
      // Clean up any existing sessions for this user and time slot
      const existingSessionsQuery = query(
        collection(db, 'activeBookings'),
        where('userId', '==', auth.currentUser.uid),
        where('appointmentDate', '==', selectedDate),
        where('appointmentTime', '==', timeSlot),
        where('appointmentType', '==', appointmentType)
      );

      const existingSessions = await getDocs(existingSessionsQuery);
      const deletePromises = existingSessions.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Create new active session
      const sessionData = {
        userId: auth.currentUser.uid,
        appointmentDate: selectedDate,
        appointmentTime: timeSlot,
        appointmentType: appointmentType,
        location: selectedLocation,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 2 * 60 * 1000), // 2 minutes
        pageLoadId: pageLoadId.current
      };

      const docRef = await addDoc(collection(db, 'activeBookings'), sessionData);
      activeSessionIdRef.current = docRef.id; // Store session ID for cleanup
      console.log('✅ Active session created for time slot:', timeSlot, '- Session ID:', docRef.id);
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating active session:', error);
      throw error;
    }
  };

  // Handle time slot selection
  const handleTimeSelect = async (timeSlot) => {
    try {
      setLoading(true);
      
      // Create active session
      const sessionId = await createActiveSession(timeSlot);
      
      // Clear the ref so cleanup doesn't delete this session
      // (it will be managed by the parent Appointment component)
      activeSessionIdRef.current = null;
      
      // Call the callback with selected time and session ID
      onTimeSelect(timeSlot, sessionId);
      
    } catch (error) {
      console.error('Error selecting time slot:', error);
      setError('Failed to select time slot. Please try again.');
      setLoading(false);
    }
  };

  // Set up real-time updates when modal opens
  useEffect(() => {
    if (!isOpen || !selectedDate || !selectedLocation || !appointmentType) {
      return;
    }

    // Initial check
    checkOccupiedTimeSlots();
    setLoading(false);

    // Set up real-time listener for orders
    const ordersQuery = query(
      collection(db, 'orders'),
      where('appointmentDate', '==', selectedDate),
      where('location', '==', selectedLocation),
      where('appointmentType', '==', appointmentType),
      where('status', 'in', ['confirmed', 'trading'])
    );

    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      console.log('🔄 Orders changed - refreshing time slots');
      checkOccupiedTimeSlots();
    });

    // Set up real-time listener for active bookings
    const activeBookingsQuery = query(
      collection(db, 'activeBookings'),
      where('appointmentDate', '==', selectedDate),
      where('location', '==', selectedLocation),
      where('appointmentType', '==', appointmentType)
    );

    const unsubscribeSessions = onSnapshot(activeBookingsQuery, (snapshot) => {
      console.log('🔄 Active sessions changed - refreshing time slots');
      checkOccupiedTimeSlots();
    });

    // Cleanup function
    return () => {
      unsubscribeOrders();
      unsubscribeSessions();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
  }, [isOpen, selectedDate, selectedLocation, appointmentType]);

  // Cleanup on unmount and handle page unload
  useEffect(() => {
    // Handle page unload/refresh - cleanup session immediately
    const handleBeforeUnload = () => {
      if (activeSessionIdRef.current) {
        console.log('🧹 Page unloading - cleaning up TimeSelectModal session');
        cleanupActiveSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      // Clean up active session when modal closes/unmounts
      cleanupActiveSession();
    };
  }, []);

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      cleanupActiveSession(); // Clean up session before closing
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="time-select-modal-overlay" onClick={handleBackdropClick}>
      <div className="time-select-modal">
        <div className="time-select-modal-header">
          <h2>Select Time</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="time-select-modal-content">
          <div className="booking-info">
            <p><strong>Date:</strong> {selectedDate}</p>
            <p><strong>Location:</strong> {selectedLocation}</p>
            <p><strong>Type:</strong> {getAppointmentTypeLabel(appointmentType)}</p>
          </div>

          <div className="time-slots-section">
            <h3>Available Time Slots</h3>
                         <p className="update-info">Updates every second • {appointmentType === 'special' ? '3 users' : '1 user'} per slot</p>
            
            {loading ? (
              <div className="loading">Loading time slots...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <div className="time-slots-grid">
                {availableTimeSlots.length > 0 ? (
                  availableTimeSlots.map((timeSlot) => (
                    <button
                      key={timeSlot}
                      onClick={() => handleTimeSelect(timeSlot)}
                      className="time-slot-button available"
                      disabled={loading}
                    >
                      {timeSlot}
                    </button>
                  ))
                ) : (
                  <div className="no-slots">
                    <p>No available time slots for this date.</p>
                    <p>Please select a different date or try again later.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {occupiedTimeSlots.size > 0 && (
            <div className="occupied-slots-section">
              <h3>Occupied Time Slots</h3>
              <div className="occupied-slots-grid">
                {Array.from(occupiedTimeSlots).map((timeSlot) => (
                  <div key={timeSlot} className="time-slot-button occupied">
                    {timeSlot}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeSelectModal;
